import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { post, put } from '../utils/api';
import { customers as customerService } from '../services/kioskApi';
import { generateInternalNoEmail, isValidEmail, sanitizeEmail } from '../utils/customerEmail';

const states = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const countries = ['United States'];

export default function AddDevoteeDialog({ open, onClose, onSuccess, onPostPayment }) {
  const [formData, setFormData] = useState({
    // Personal Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    spouse: '',
    familyMembers: '',
    gothram: '',
    // Address
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null); // { id, message }
  const [noEmail, setNoEmail] = useState(false);
  const [createdCustomer, setCreatedCustomer] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'email' ? sanitizeEmail(value) : value }));
    setError('');
  };

  const handleNoEmailToggle = (e) => {
    const checked = Boolean(e.target.checked);
    if (checked) {
      if (!formData.firstName || !formData.lastName) {
        setError('Enter First Name and Last Name before selecting "User has no email".');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        email: generateInternalNoEmail(prev.firstName, prev.lastName)
      }));
    } else {
      setFormData((prev) => ({ ...prev, email: '' }));
    }
    setNoEmail(checked);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    const effectiveEmail = noEmail
      ? (formData.email || generateInternalNoEmail(formData.firstName, formData.lastName))
      : sanitizeEmail(formData.email);

    if (!formData.firstName || !formData.lastName || !effectiveEmail) {
      setError('First Name, Last Name, and Email are required');
      return;
    }

    if (!isValidEmail(effectiveEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      // Step 1: Create customer with basic info
      const customerData = {
        email: effectiveEmail,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone || ''
      };

      // The create endpoint is LDB-first: the local customer id comes back immediately (usable now)
      // and the WC sync happens out-of-band.
      const createdCustomer = await customerService.create(customerData);
      const customerId = createdCustomer.id || createdCustomer.customer_id || createdCustomer.user_id;
      if (!customerId) {
        throw new Error('Failed to create customer: No ID returned');
      }

      // Step 2: Update with address and meta fields if provided
      const hasAddress = formData.address1 || formData.city || formData.state || formData.postalCode;
      const hasMeta = formData.spouse || formData.familyMembers || formData.gothram;
      
      if (hasAddress || hasMeta) {
        const updatePayload = {
          ...(hasAddress && {
            billing: {
              ...(formData.address1 && { address_1: formData.address1 }),
              ...(formData.address2 && { address_2: formData.address2 }),
              ...(formData.city && { city: formData.city }),
              ...(formData.state && { state: formData.state }),
              ...(formData.postalCode && { postcode: formData.postalCode }),
              ...(formData.country && { country: formData.country === 'United States' ? 'US' : formData.country }),
              phone: formData.phone || ''
            }
          }),
          ...(formData.spouse && { spouse: formData.spouse }),
          ...(formData.familyMembers && { family_members: formData.familyMembers }),
          ...(formData.gothram && { gothram: formData.gothram })
        };

        try {
          await put(`/devotees/${customerId}`, updatePayload);
        } catch (updateErr) {
          console.warn('Failed to update address/meta fields, but customer was created:', updateErr);
          // Don't fail the whole operation if update fails - customer was created successfully
        }
      }

      // Success - show confirmation with ID, but don't close automatically
      setSuccess({
        id: customerId,
        message: `Devotee created successfully with ID: ${customerId}`
      });
      setCreatedCustomer(createdCustomer);
      setError(''); // Clear any previous errors

      // Call onSuccess callback for parent component (e.g., to refresh search)
      if (onSuccess) {
        onSuccess(createdCustomer);
      }
    } catch (err) {
      console.error('Error creating devotee:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to create devotee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form and state when closing
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        spouse: '',
        familyMembers: '',
        gothram: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'United States'
      });
      setNoEmail(false);
      setError('');
      setSuccess(null);
      setCreatedCustomer(null);
      onClose();
    }
  };

  const handlePostPayment = () => {
    if (!createdCustomer || typeof onPostPayment !== 'function') return;
    onPostPayment(createdCustomer);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Add New Devotee</Typography>
          <IconButton onClick={handleClose} disabled={loading} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success.message}
            </Alert>
          )}

          {/* Personal Details Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 1 }}>
            Personal Details
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            disabled={loading || !!success}
            size="small"
          />
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              disabled={loading || !!success}
              size="small"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading || !!success}
              size="small"
              InputProps={{ readOnly: noEmail }}
            />
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={loading || !!success}
              size="small"
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={noEmail}
                onChange={handleNoEmailToggle}
                disabled={loading || !!success}
              />
            }
            label="User has no email"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Spouse"
            name="spouse"
            value={formData.spouse}
            onChange={handleChange}
            disabled={loading || !!success}
            size="small"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Family Members (comma separated)"
            name="familyMembers"
            value={formData.familyMembers}
            onChange={handleChange}
            disabled={loading || !!success}
            size="small"
            placeholder="e.g., Sahithi, Sri Ganesh"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Gothram"
            name="gothram"
            value={formData.gothram}
            onChange={handleChange}
            disabled={loading || !!success}
            size="small"
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 2 }} />

          {/* Address Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Address
          </Typography>

          <TextField
            fullWidth
            label="Address 1"
            name="address1"
            value={formData.address1}
            onChange={handleChange}
            disabled={loading || !!success}
            size="small"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Address 2"
            name="address2"
            value={formData.address2}
            onChange={handleChange}
            disabled={loading || !!success}
            size="small"
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              disabled={loading || !!success}
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select
                name="state"
                value={formData.state}
                onChange={handleChange}
                label="State"
                disabled={loading || !!success}
              >
                {states.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Postal Code"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              disabled={loading || !!success}
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select
                name="country"
                value={formData.country}
                onChange={handleChange}
                label="Country"
                disabled={loading || !!success}
              >
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {success ? (
            <>
              <Button onClick={handleClose} disabled={loading}>
                Close
              </Button>
              {typeof onPostPayment === 'function' && (
                <Button
                  variant="contained"
                  onClick={handlePostPayment}
                  disabled={loading || !createdCustomer}
                >
                  Post Payment
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !formData.firstName || !formData.lastName || (!noEmail && !formData.email)}
              >
                {loading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Creating...
                  </>
                ) : (
                  'Create Devotee'
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

